import React from "react";
import { t } from "./CustomComponent.js";
import { fmtDate, fmtDateTime } from "../utils/timeTracking.js";

// ชิ้นส่วนที่ใช้ร่วมกันของหน้า "ประวัติทะเบียนสินทรัพย์" (admin ภาพรวม/ค้นหา/Asset 360 และฝั่งลูกค้า)
// field ตรงกับ contract admin/asset-history-report/* และ customer/asset-history/*

export const fill = (text, values) => Object.keys(values).reduce((acc, k) => acc.replace(`{${k}}`, values[k]), text);

export const fmtInt = (n) => (Number(n) || 0).toLocaleString("en-US");
export const fmtBaht = (n) => `${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ฿`;
// ค่าที่ API ไม่มี (null เช่น ค่าแรง) แสดง "-"
export const fmtBahtOrDash = (n) => (n === null || n === undefined ? "-" : fmtBaht(n));
// ISO UTC -> DD/MM/YYYY เวลาไทย
export const fmtDay = (iso) => {
  const s = fmtDateTime(iso);
  return s === "-" ? "-" : s.slice(0, 10);
};
export { fmtDate, fmtDateTime };

export const WARRANTY_STATUSES = ["in", "near", "expired", "none"];
export const WARRANTY_COLORS = { in: "#21B25A", near: "#F5B70A", expired: "#EF6B6B", none: "#C9D1DE" };
export const TYPE_COLORS = { ins: "#0E9AA7", cm: "#F07F1B", pm: "#3B82F6", wr: "#A855F7", pool: "#0E9AA7" };
export const TIMELINE_TYPES = ["cm", "wr", "pm", "ins", "pool"];

export const StatusBadge = ({ status }) => {
  const active = status === "active";
  return (
    <span className={`ah-bd ${active ? "act" : "ina"}`}>
      <span className="ah-dot" style={{ background: active ? "#21B25A" : "#8C96A6" }} />
      {t(active ? "ah_s_active" : "ah_s_inactive")}
    </span>
  );
};

// ป้ายสถานะประกัน (ครั้งล่าสุด) + จำนวนวันคงเหลือ
export const WarrantyBadge = ({ warranty, withDays = true }) => {
  const status = WARRANTY_STATUSES.includes(warranty?.status) ? warranty.status : "none";
  let label = t(`ah_w_${status}`);
  const days = warranty?.days_left;
  if (withDays && days !== null && days !== undefined) {
    if (status === "in" || status === "near") label += ` · ${fill(t("ah_days_left"), { n: fmtInt(days) })}`;
    else if (status === "expired") label += ` · ${fill(t("ah_days_over"), { n: fmtInt(Math.abs(days)) })}`;
  }
  return <span className={`ah-bd ${status}`}>{label}</span>;
};

export const TypeBadge = ({ type }) => <span className={`ah-bd t-${type}`}>{t(`ah_type_${type}`)}</span>;

export const OpenBadge = ({ count }) => (
  <span className="ah-bd open">● {count > 0 ? fill(t("ah_open_n"), { n: fmtInt(count) }) : t("ah_open")}</span>
);

export const SapTag = () => null; // ไม่แสดงป้าย "รอ SAP" (ตามการ์ด)

export const EmptyBox = ({ icon = "🗂️", title, hint, children }) => (
  <div className="ah-empty">
    <div className="ic">{icon}</div>
    <div className="fw-semibold">{title}</div>
    {hint ? <div className="text-12 mt-1">{hint}</div> : null}
    {children ? <div className="mt-3">{children}</div> : null}
  </div>
);

export const Skeleton = ({ heights = [120, 260, 200] }) => (
  <div data-testid="ah-skeleton">
    {heights.map((h, i) => <div key={i} className="ah-skel" style={{ height: h }} />)}
  </div>
);

// การ์ด error (E10) + ปุ่มลองใหม่
export const ErrorCard = ({ message, onRetry }) => (
  <div className="bg-white rounded-4 shadow-sm p-5 mb-4 text-center" data-testid="ah-error">
    <h5 className="fw-bold text-dark">{t("ah_error_title")}</h5>
    <p className="text-secondary text-14 mb-3">{message || t("ah_error_hint")}</p>
    {onRetry ? <button type="button" className="btn btn-primary px-4 py-2 fw-bold rounded-3" onClick={onRetry}>{t("ah_retry")}</button> : null}
  </div>
);

// ประวัติการรับประกันหลายครั้ง: แถบสี used_percent (>75 เหลือง) / ended = เทา
export const WarrantyBars = ({ items }) => {
  if (!items?.length) return <EmptyBox icon="🛡️" title={t("ah_no_warranty_history")} />;
  const stateBadge = {
    active: <span className="ah-bd in">{t("ah_wh_active")}</span>,
    ended: <span className="ah-bd none">{t("ah_wh_ended")}</span>,
    upcoming: <span className="ah-bd near">{t("ah_wh_upcoming")}</span>,
    unknown: <span className="ah-bd none">{t("ah_w_none")}</span>,
  };
  return (
    <div className="d-flex flex-column gap-3" data-testid="ah-warranty-bars">
      {items.map((w) => {
        const pct = Math.min(100, Math.max(0, Number(w.used_percent) || 0));
        const color = w.state === "ended" ? "#C9D1DE" : pct > 75 ? "#F5B70A" : "#21B25A";
        return (
          <div className="ah-wr" key={w.no}>
            <div>
              <b>{fill(t("ah_wh_no"), { n: w.no })}</b>
              <div className="text-12 text-secondary">{w.months ? fill(t("ah_wh_months"), { n: w.months }) : ""}</div>
            </div>
            <div>
              <div className="bar"><div style={{ width: `${w.state === "upcoming" ? 0 : pct}%`, background: color }} /></div>
              <div className="text-12 text-secondary mt-1">{fmtDate(w.start_date)} → {fmtDate(w.end_date)}{w.remark ? ` · ${w.remark}` : ""}</div>
            </div>
            <div className="text-end">{stateBadge[w.state] || stateBadge.unknown}</div>
          </div>
        );
      })}
    </div>
  );
};
